'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { requestGetSpcNavigation } from '@apis/renewal/spc';
import { PAGE_CONSTANT } from '@constants/page';
import useGa4Event from '@hooks/useGa4Event';
import useHistoryRouter from '@hooks/useLinkTo';
import { SpcNavigationItem } from '@models/spc';
import EvtNaviItemType2 from '@wconcept/wconcept-ui/components/molecules/_item/EvtNaviItemType2';

const SPC_GA4_AREA = 'spc_main_tab';

interface UseSpcNavigationModuleParams {
  spcType: string;
}

const sortNavigationItems = (items: SpcNavigationItem[]): SpcNavigationItem[] =>
  [...items].sort((a, b) => a.navSort - b.navSort);

const getSpcLandingUrl = (displaySpecialCd: string): string =>
  `${process.env.NEXT_PUBLIC_WCONCEPT_DISPLAY_WEB}/rn/spc/${displaySpecialCd}`;

export const isSpcNavigationEnabled = (spcType: string): boolean =>
  PAGE_CONSTANT.SPC.NAVIGATION_SPC_TYPES.some(
    type => type.toLowerCase() === spcType.toLowerCase()
  );

export default function useSpcNavigationModule({
  spcType,
}: UseSpcNavigationModuleParams) {
  const [naviData, setNaviData] = useState<SpcNavigationItem[]>([]);
  const [activeSection, setActiveSection] = useState<number | undefined>(
    undefined
  );
  const [isNavigationResolved, setIsNavigationResolved] = useState(
    !isSpcNavigationEnabled(spcType)
  );
  const { sendGa4TabClick } = useGa4Event();
  const { linkTo } = useHistoryRouter();
  const isNavigationEnabled = isSpcNavigationEnabled(spcType);

  const getNaviData = useCallback(async () => {
    if (!isNavigationEnabled) {
      setIsNavigationResolved(true);
      return;
    }

    try {
      const response = await requestGetSpcNavigation();
      const sortedItems = sortNavigationItems(response);
      const findIndex = sortedItems.findIndex(
        item => item.displaySpecialCd.toLowerCase() === spcType.toLowerCase()
      );

      if (findIndex !== -1) {
        setActiveSection(findIndex);
      }
      setNaviData(sortedItems);
    } finally {
      setIsNavigationResolved(true);
    }
  }, [isNavigationEnabled, spcType]);

  useEffect(() => {
    getNaviData();
  }, [getNaviData]);

  const handleClick = useCallback(
    (index: number) => {
      const selectedItem = naviData[index];
      if (!selectedItem) return;

      sendGa4TabClick({
        text: selectedItem.navTitle,
        area: SPC_GA4_AREA,
        listNo: index + 1,
      });

      const landingUrl = getSpcLandingUrl(selectedItem.displaySpecialCd);

      try {
        const url = new URL(landingUrl);
        const gnbType = new URLSearchParams(window.location.search).get(
          'gnbType'
        );

        if (gnbType) {
          url.searchParams.set('gnbType', gnbType);
        }

        if (url.pathname !== window.location.pathname) {
          linkTo.push({ url: url.toString() });
        }
      } catch (error) {
        console.error(error);
      }
    },
    [linkTo, naviData, sendGa4TabClick]
  );

  const items = useMemo(
    () =>
      naviData.map((item, index) => (
        <EvtNaviItemType2
          key={item.displaySpecialCd}
          active={index === activeSection}
          title={item.navTitle}
          $offBgColor={item.offBgColorCd}
          $onBgColor={item.onBgColorCd}
          $offTextColor={item.offTextColorCd}
          $onTextColor={item.onTextColorCd}
          onClick={() => {
            handleClick(index);
          }}
        />
      )),
    [activeSection, handleClick, naviData]
  );

  return {
    items,
    activeSection,
    hasNavigation: isNavigationEnabled && naviData.length > 1,
    isNavigationResolved,
  } as const;
}
